import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AppStatusService} from './app-status.service';
import {ApiService} from './api.service';
import {noTheme} from '../@models/themes/theme.fun';
import {AppStatus, DoorStates} from '../@models/app-status';
import {Theme} from '../@models/themes/theme';
import {VmcState} from '../../shared-gen/Vending/Status/VmcState';

/**
 * By listening to app status changes, this services navigates to the right pages.
 * Call `start()` to run the service.
 */
@Injectable({
	providedIn: 'root'
})
export class NavigatorService {

	private readonly interventionPageDelayS = 0; // TODO: remove completely. we can go to Intervention immediately

	private doNotNavigate = false;

	private theme: Theme = noTheme;
	private lastVmcState = VmcState.Intervention;
	private doors: DoorStates = {};
	private interventionPageTimer = 0;

	constructor(private appStatusService: AppStatusService,
	            private router: Router,
	            private api: ApiService) {
	}

	start() {
		this.appStatusService.appStatus.subscribe(it => {
			this.onAppStatusChanged(it);
		});
	}

	setDoNotNavigate(doNotNavigate: boolean){
		this.doNotNavigate = doNotNavigate;
	}

	/**
	 * Navigates to the given page url (e.g. "/dallmayr/menu") and
	 * reports user activity to the server.
	 */
	openPageAndReportUserActivity(pageUrl: string) {
		this.router.navigate([pageUrl]);
		this.appStatusService.reportUserActivity();
	}

	/**
	 * Cancels the current order (if any) and opens the menu page.
	 * When returnCredit is true, the already paid credit is returned
	 * (i.e. pay out hard cash and release vouchers).
	 */
	async cancelOrder(returnCredit: boolean = true) {
		await this.api.orderApi.cancel(returnCredit);
		await this.appStatusService.reportUserActivity();
	}

	private onAppStatusChanged(appStatus: AppStatus) {
		this.theme = appStatus.theme;
		const device = appStatus.deviceStatus;

		if (device.VendingStatus == null)
			return;
		this.lastVmcState = device.VendingStatus.VmcState;

		this.doors = device.Doors;
		// To avoid deadlock situations, always check if we have to switch the page
		this.updatePage();
	}

	public updatePage() {
		this.updatePageInternal();
	}

	public leaveUpdatePage() {
		this.updatePageInternal(true);
	}

	private updatePageInternal(leaveUpdatePage = false) {
		if (this.doNotNavigate) return;

		const currentPage = this.router.url;
		const vmcState = this.lastVmcState;
		let newPage: string = null;

		// Do not leave the update page by the navigator service. The app navigated
		// there manually and will leave it manually.
		if (currentPage === this.theme.updatePage && false == leaveUpdatePage)
			return;

		// When the state is intervention, do not open intervention page immediately
		// but after a short delay (to avoid very short intervention popups)
		if (vmcState === 'Intervention' && !this.interventionPageTimer) {
			this.interventionPageTimer = window.setTimeout(() => {
				this.interventionPageTimer = 0;
				const warningPage = (this.theme || noTheme).warningPage;
				console.log('Open warning page for intervention (delayed by ' +
					this.interventionPageDelayS + ' seconds)');
				this.router.navigate([warningPage]);
			}, this.interventionPageDelayS * 1000);
		} else if (vmcState !== 'Intervention' && this.interventionPageTimer) {
			clearTimeout(this.interventionPageTimer);
			this.interventionPageTimer = 0;
		}

		// When the state is cleaning or initializing, open error page
		if (vmcState === 'Cleaning' || vmcState === 'Initializing')
			newPage = this.theme.warningPage;

		// When the state is Inactive, send user action and open menu page
		if (vmcState === 'Inactive')
			newPage = this.theme.menuPage;

		// When the state is Ready and we are not on the options page, open menu page
		if (vmcState === 'Ready' && currentPage !== this.theme.optionsPage)
			newPage = this.theme.menuPage;

		// When the state is Paying, open paying page, if there is one
		if (vmcState === 'Paying' && this.theme.payingPage)
			newPage = this.theme.payingPage;

		// When the state is CupPlacing, open cup page, if there is one.
		// If not, skip this step.
		if (vmcState === 'CupPlacing') {
			if (this.theme.cupPage)
				newPage = this.theme.cupPage;
			else
				this.api.deviceApi.skipCup();
		}

		// When the state is serving or served, open serving page
		//if ((['Serving', 'Served'] as VmcState[]).indexOf(vmcState) !== -1)
		if (vmcState === 'Serving')
			newPage = this.theme.servingPage;

		if (vmcState === 'Served') {
			if (this.theme.servedPage) {
				newPage = this.theme.servedPage;
			}else{
				newPage = this.theme.servingPage;
			}
		}

		// When the state is failed, open failed page
		if ((['Failed'] as VmcState[]).indexOf(vmcState) !== -1)
			newPage = this.theme.failedPage;

		/* OLD: // When the machine or fridge door is open, go to intervention screen immediately
		if (this.doors && (this.doors['MachineDoor'] === 'Open' || this.doors['FridgeDoor'] === 'Open'))
			newPage = this.theme.warningPage;
		*/
		// When the state is maintenance, open maintenance page (or intervention page, if missing)
		if (vmcState === VmcState.Maintenance) {
			newPage = this.theme.maintenancePage || this.theme.warningPage;
		}

		// Page changed? Then open it.
		if (newPage && newPage !== currentPage) {
			console.log('newPage: ' + newPage + ' (from ' + currentPage + ') in state ' + vmcState);
			this.router.navigate([newPage]);
		}
	}

}
